/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Editor } from "@tiptap/react";
import dayjs from "dayjs";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import {
  atom,
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from "recoil";
import { ContactApi } from "../api/Contacts";
import {
  ICreateEmail,
  checkScheduleTimeSimilarity,
  createDraftEmail,
  createEmail,
} from "../api/Email";
import { LinksAPI } from "../api/Links";
import { TagApi } from "../api/Tags";
import { ActiveEmailAtom } from "../atoms/ActiveEmailAtom";
import {
  DelayTimeAtom,
  DelayTimeIsOverAtom,
  DelayTimeToEndAtom,
} from "../atoms/DelayTimeAtom";
import { FullScreenAtom } from "../atoms/FullScreenAtom";
import { LinksAtom } from "../atoms/LinksAtom";
import { RecipientAtom, RecipientAtomValue } from "../atoms/RecipientAtom";
import { CountedSavedSearchesAtom } from "../atoms/SavedSearches";
import { ScheduleDateAtom } from "../atoms/ScheduleDateAtom";
import { TagsAtom } from "../atoms/TagsAtom";
import { TestDriveBannerAtom } from "../atoms/TestDriveBannerAtom";
import { InternalUserAtom, UserAtom } from "../atoms/UserAtom";
import VerifyingEmailDNS from "../components/DNSModal/VerifyingEmailDNS";
import EmailEditor, { EditorHandle } from "../components/EmailEditor";
import { useModal } from "../components/Modal/Modal";
import PreviewEmailWithTimer from "../components/Modal/container/PreviewEmailWithTimer";
import Warning from "../components/Modal/container/Warning";
import BottomBar from "../components/NewEmail/BottomBar";
import { CurrentRecipientTagsAtom } from "../components/NewEmail/Modals/Recipients";
import ScheduleBar from "../components/NewEmail/ScheduleBar";
import TopBar from "../components/NewEmail/TopBar";
import { ITagQuery, IUserIRoute } from "../generated/Api";
import { useDefaultAlias } from "../hook/useDefaultAlias";
import useDidMountEffect from "../hook/useDidMountEffect";
import useSetEmail from "../hook/useSetEmail";
import { ILink } from "../types/LinkTypes";
import {
  localStorageGetItem,
  localStorageRemoveItem,
  localStorageSetItem,
} from "../utils/LocalStorage";
import SeoHead from "./../components/SeoHead";

const normalContent = css`
  height: 100%;
  width: calc(100vw - 60px);
  padding-top: 87px;
  padding-right: 380px;
  padding-bottom: 50px;

  position: relative;

  @media screen and (max-width: 1080px) {
    padding-right: 0;
  }

  @media screen and (max-width: 960px) {
    padding-top: 0px;
    position: fixed;
    top: 153px;
  }

  @media screen and (max-width: 560px) {
    width: 100vw;
  }
`;

const fullScreeContent = css`
  height: calc(100% - 50px);
  width: 100%;
`;

export const Content = styled.div<{ fullScreen: boolean }>`
  ${({ fullScreen }) => (fullScreen ? fullScreeContent : normalContent)};
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const Body = styled.div<{ isTestDriverUser: boolean }>`
  width: 100vw;
  height: 100vh;

  @media screen and (max-width: 650px) {
    position: relative !important;

    height: ${(props) =>
      props.isTestDriverUser ? "calc(100vh - 148px)" : "100vh"};
  }
`;

// A function made only to persist and reload Recoil values,
// isolated as a component as not to trigger re-renders
function RecoilPersistence() {
  const currentRecipient = useRecoilValue(CurrentRecipientTagsAtom);
  const recipient = useRecoilValue(RecipientAtom);
  const user = useRecoilValue(UserAtom);

  useEffect(() => {
    if (recipient && recipient.count && recipient.count > 0) {
      localStorageSetItem(
        "ATOM_RECIPIENT",
        JSON.stringify(recipient),
        user.email
      );
      if (recipient.setted === "tagquery") {
        localStorageSetItem(
          "ATOM_CURRENT_RECIPIENTS",
          JSON.stringify(currentRecipient),
          user.email
        );
      }
    }
  }, [recipient]);

  return null;
}

export function getNewTime(delayTime: Date) {
  const time = dayjs(delayTime);
  const now = Date.now();
  const hrs = time.diff(now, "hour");
  const min = time.diff(now, "minutes");
  const sec = time.diff(now, "seconds");

  return {
    min: min - hrs * 60,
    sec: sec - min * 60,
  };
}

export function getBody(
  editor: Editor,
  user: IUserIRoute & {
    enableBetaFeatures?: boolean;
    billing: {
      admins?: number;
      externalUsers?: number;
      active?: boolean;
      testDriveExpiration?: number;
      testDriveActivated?: boolean;
    };
  }
) {
  if (!editor) return "";

  let editorHTML = editor.getHTML();
  while (editorHTML.includes("></p>")) {
    editorHTML = editorHTML.replace("></p>", ">&nbsp;</p>");
  }

  while (editorHTML.includes("></h1>")) {
    editorHTML = editorHTML.replace("></h1>", ">&nbsp;</h1>");
  }

  while (editorHTML.includes("></h2>")) {
    editorHTML = editorHTML.replace("></h2>", ">&nbsp;</h2>");
  }

  while (editorHTML.includes("></h3>")) {
    editorHTML = editorHTML.replace("></h3>", ">&nbsp;</h3>");
  }

  let body = `<pre style="overflow-wrap: break-word; white-space: pre-wrap; 
  line-height: 1.5em;
  letter-spacing: 0.025em;
  font-family: ${
    user?.emailOptions.font.family ? user.emailOptions.font.family : "Arial"
  }; font-size:${
    user?.emailOptions.font.size || 14
  }px; width: 100%; max-width: ${
    user?.emailOptions.maxWidth ? `${user?.emailOptions.maxWidth}px` : "100%"
  }">${editorHTML}</pre>`;

  return body;
}

const IsLoadingSendEmailAtom = atom<boolean>({
  key: "IsLoadingSendEmailAtom",
  default: false,
});

export default function NewEmailScreen() {
  const router = useRouter();
  const { verifyDNS } = router.query;
  const showTestDrive = useRecoilValue(TestDriveBannerAtom);
  const [newEmail, setNewEmail] = useRecoilState(ActiveEmailAtom);
  const fullScreen = useRecoilValue(FullScreenAtom);
  const scheduleDate = useRecoilValue(ScheduleDateAtom);
  const resetScheduleDate = useResetRecoilState(ScheduleDateAtom);
  const setCurrentRecipient = useSetRecoilState(CurrentRecipientTagsAtom);
  const [recipient, setRecipient] = useRecoilState(RecipientAtom);
  const modal = useModal();
  const setDelayTime = useSetRecoilState(DelayTimeAtom);
  const setDelayTimeToEnd = useSetRecoilState(DelayTimeToEndAtom);
  const setDelayTimeIsOver = useSetRecoilState(DelayTimeIsOverAtom);
  const savedSearches = useRecoilValue(CountedSavedSearchesAtom);

  const [isLoadingRecipient, setIsLoadingRecipient] = useState(true);
  const [isLoading, setIsLoading] = useRecoilState(IsLoadingSendEmailAtom);
  const user = useRecoilValue(UserAtom);
  const internalUser = useRecoilValue(InternalUserAtom);
  const editorHandlerRef = useRef<EditorHandle>(null);
  const resetActiveEmail = useResetRecoilState(ActiveEmailAtom);

  const setTags = useSetRecoilState(TagsAtom);
  const setLinks = useSetRecoilState<ILink[]>(LinksAtom);

  const isTestDriverUser = showTestDrive.show;

  const [isScheduleBarOpen, setIsScheduleBarOpen] = useState(false);

  const editor = editorHandlerRef.current?.getEditor();

  const { resetEmailContent } = useSetEmail("new-email", editor);

  const { defaultAlias } = useDefaultAlias({
    user,
    internalUser,
  });

  useEffect(() => {
    if (user?.address === "" && user?.company === "") {
      router.replace("/complete-onboarding");
    }
  }, [user]);

  useEffect(() => {
    (async () => {
      const linksPool = await LinksAPI.readAll();
      if (linksPool) {
        setLinks(linksPool as unknown as ILink[]);
      }
    })();
    (async () => {
      const tagsPool = await TagApi.readAll({ getCampaigns: true });
      if (tagsPool) {
        setTags(tagsPool);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      setIsLoadingRecipient(true);
      let JSONCurrentRecipients = localStorageGetItem(
        "ATOM_CURRENT_RECIPIENTS",
        user.email
      );
      let JSONRecipient = localStorageGetItem("ATOM_RECIPIENT", user.email);

      if (JSONCurrentRecipients) {
        let currentRecipient = JSON.parse(JSONCurrentRecipients as string);
        setCurrentRecipient(currentRecipient);
      }

      if (JSONRecipient) {
        let recipientFromLocal: RecipientAtomValue = JSON.parse(
          JSONRecipient as string
        );

        setRecipient(recipientFromLocal);

        if (recipientFromLocal.tagQuery) {
          const [contactsCount] = await ContactApi.countContactsByTagQueries([
            recipientFromLocal.tagQuery,
          ]);

          setNewEmail((newEmail) => ({
            ...newEmail,
            tagQuery: recipientFromLocal.tagQuery as ITagQuery,
          }));

          setRecipient({
            ...recipientFromLocal,
            count: contactsCount,
          });
        }
      }

      setIsLoadingRecipient(false);
    })();
  }, []);

  useEffect(() => {
    return () => {
      resetActiveEmail();
    };
  }, []);

  useDidMountEffect(() => {
    localStorageSetItem("DRAFT_EMAIL_SUBJECT", newEmail.subject);
  }, [newEmail.subject]);

  useDidMountEffect(() => {
    localStorageSetItem("DRAFT_EMAIL_SPLIT_SUBJECT", newEmail.splitSubject);
  }, [newEmail.splitSubject]);

  useDidMountEffect(() => {
    localStorageSetItem("DRAFT_EMAIL_LABELS", JSON.stringify(newEmail.labels));
  }, [newEmail.labels]);

  // Check if delay option is active
  function hasDelay() {
    if (user?.emailOptions?.delayed && user.emailOptions.delayed !== "0")
      return true;
    return false;
  }

  async function handleCreateScheduleEmail() {
    const { tagQuery, searchId } = recipient;

    let body = getBody(editor, user);

    const scheduleNewEmail = {
      ...newEmail,
      body,
      scheduleTo: dayjs(scheduleDate).add(30, "seconds").toDate(),
    };

    let selectedFooterId = recipient.footerId
      ? recipient.footerId
      : user.emailOptions.footers[0].id;

    let selectedAliasId = defaultAlias.id;

    let selectedSavedSearch = savedSearches.find((ss) => ss.id === searchId);

    const response = await createEmail(
      {
        ...scheduleNewEmail,
        footerId: selectedFooterId,
        aliasId: selectedAliasId,
        tagQuery: tagQuery as ITagQuery,
        searchId,
        defaultModeColorLink: user.emailOptions.defaultModeColorLink,
        darkModeColorLink: user.emailOptions.darkModeColorLink,
        labels: Array.from(
          new Set([
            ...scheduleNewEmail.labels,
            ...(selectedSavedSearch?.labels || []),
          ])
        ),

        showOnArchive:
          typeof recipient.showOnArchive === "undefined"
            ? user.emailOptions.showOnArchive
            : recipient.showOnArchive,
        ...(scheduleNewEmail.splitMode
          ? {
              splitSubject: scheduleNewEmail.splitSubject,
              splitMode: scheduleNewEmail.splitMode,
            }
          : { splitSubject: undefined, splitMode: undefined }),
      },
      "schedule"
    );

    if (response) {
      resetEmailContent.current();
      resetScheduleDate();
      toast("Your email was scheduled succesfully", {
        type: "success",
      });
      localStorageRemoveItem("DRAFT_INTEREST_TAGS");
    }
  }

  async function handleSchedule() {
    const { subject } = newEmail;
    const { tagQuery } = recipient;

    if (!editor) return null;

    if (subject.trim() && !editor.isEmpty && tagQuery && !isLoading) {
      setIsLoading(true);
      try {
        const scheduleTo = dayjs(scheduleDate).add(30, "seconds").toDate();

        if (dayjs(scheduleTo).isBefore(dayjs())) {
          toast("Date invalid", { type: "info" });
          return;
        }

        const isSimilar = await checkScheduleTimeSimilarity(scheduleTo);

        if (isSimilar) {
          modal.pushModal(
            <Warning
              icon={"./icons/modal/email.svg"}
              title={`You have another email scheduled at similar time`}
              description="Are you sure you want to schedule this one too?"
              buttonsName={["No", "Yes"]}
              onOkClicked={() => {
                handleCreateScheduleEmail();
                modal.popAll();
              }}
              onCancelClicked={() => modal.popAll()}
            />
          );
        } else {
          handleCreateScheduleEmail();
          modal.popAll();
        }
      } catch (err) {
        toast("Your email could not be scheduled", { type: "error" });
      }
      setIsLoading(false);
    } else {
      toast("Title and text required", { type: "info" });
      modal.popAll();
    }
  }

  async function handleSend() {
    const { subject } = newEmail;
    const { tagQuery, searchId } = recipient;

    if (isLoading) return;

    if (subject && !editor?.isEmpty && tagQuery && !isLoading) {
      setIsLoading(true);

      try {
        if (!editor) return null;
        if (!editor.getHTML() && editor.getHTML() !== "") {
          toast("You need a subject and body", { type: "info" });
          return;
        }

        let body = getBody(editor, user);

        let newEmailSend: ICreateEmail = {
          ...newEmail,
          body,
        };

        if (hasDelay()) {
          newEmailSend = {
            ...newEmailSend,
            scheduleTo: dayjs()
              .add(
                parseInt(user!.emailOptions!.delayed as string) + 60,
                "seconds"
              )
              .toDate(),
          };

          const delayTimeDate = dayjs()
            .add(parseInt(user!.emailOptions!.delayed as string), "seconds")
            .toDate();

          setDelayTime(delayTimeDate);

          setDelayTimeToEnd(getNewTime(delayTimeDate));
          setDelayTimeIsOver(false);
        }

        let selectedFooterId = recipient.footerId
          ? recipient.footerId
          : user.emailOptions.footers[0].id;

        let selectedAliasId = defaultAlias.id;

        let selectedSavedSearch = savedSearches.find(
          (ss) => ss.id === searchId
        );

        const response = await createEmail(
          {
            ...newEmailSend,
            footerId: selectedFooterId,
            aliasId: selectedAliasId,
            tagQuery: tagQuery as ITagQuery,
            searchId,
            defaultModeColorLink: user.emailOptions.defaultModeColorLink,
            darkModeColorLink: user.emailOptions.darkModeColorLink,
            labels: Array.from(
              new Set([
                ...newEmailSend.labels,
                ...(selectedSavedSearch?.labels || []),
              ])
            ),
            showOnArchive:
              typeof recipient.showOnArchive === "undefined"
                ? user.emailOptions.showOnArchive
                : recipient.showOnArchive,
            ...(newEmailSend.splitMode
              ? {
                  splitSubject: newEmailSend.splitSubject,
                  splitMode: newEmailSend.splitMode,
                }
              : { splitSubject: undefined, splitMode: undefined }),
          },
          hasDelay() ? "schedule" : "sent"
        );

        if (response) {
          if (hasDelay()) {
            modal.popModal();
            modal.pushModal(
              <PreviewEmailWithTimer
                editor={editor}
                subject={newEmail.subject}
                body={body}
                user={user}
                internalUser={internalUser}
                emailInfo={{
                  footerId: selectedFooterId,
                  defaultModeColorLink: user.emailOptions.defaultModeColorLink,
                }}
                emailId={response._id}
                mode={"new-email"}
              />,
              "",
              true
            );
          } else {
            toast("Your email was sent succesfully", {
              type: "success",
            });
            modal.popModal();
            resetEmailContent.current();
            resetScheduleDate();
          }
        }
        localStorageRemoveItem("DRAFT_INTEREST_TAGS");
      } catch (err) {
        toast("Your email could not be sent", { type: "error" });
      }
      setIsLoading(false);
    } else {
      toast("Title and text required", { type: "info" });
      modal.popAll();
    }
  }

  async function handleSaveDraft() {
    const { tagQuery, searchId } = recipient;

    if (isLoading) return;

    setIsLoading(true);

    try {
      if (!editor) return null;

      let body = getBody(editor, user);

      let newEmailSend: ICreateEmail = {
        ...newEmail,
        body: body || "",
        subject: newEmail.subject || "",
      };

      let selectedFooterId = recipient.footerId
        ? recipient.footerId
        : user.emailOptions.footers[0].id;

      let selectedAliasId = defaultAlias.id;

      let selectedSavedSearch = savedSearches.find((ss) => ss.id === searchId);

      await createDraftEmail({
        ...newEmailSend,
        footerId: selectedFooterId,
        aliasId: selectedAliasId,
        tagQuery: tagQuery ? (tagQuery as ITagQuery) : undefined,
        searchId,
        defaultModeColorLink: user.emailOptions.defaultModeColorLink,
        darkModeColorLink: user.emailOptions.darkModeColorLink,
        labels: Array.from(
          new Set([
            ...newEmailSend.labels,
            ...(selectedSavedSearch?.labels || []),
          ])
        ),
        showOnArchive:
          typeof recipient.showOnArchive === "undefined"
            ? user.emailOptions.showOnArchive
            : recipient.showOnArchive,
        ...(newEmail.splitMode
          ? {
              splitSubject: newEmail.splitSubject,
              splitMode: newEmail.splitMode,
            }
          : { splitSubject: undefined, splitMode: undefined }),
      });

      toast("Your email was saved successfully", { type: "success" });

      modal.popModal();
      resetEmailContent.current();
      resetScheduleDate();

      localStorageRemoveItem("DRAFT_INTEREST_TAGS");
    } catch (err) {
      toast("For some reason your email was not saved", {
        type: "warning",
      });
    }
    setIsLoading(false);
  }

  return (
    <>
      <SeoHead pageName="New Email" />
      <Body isTestDriverUser={isTestDriverUser}>
        {verifyDNS && <VerifyingEmailDNS />}
        <RecoilPersistence />
        {!fullScreen && (
          <TopBar
            onOpen={() => setIsScheduleBarOpen(true)}
            subject={newEmail.subject}
            body={getBody(editor, user)}
            editor={editor}
          />
        )}
        <Content fullScreen={fullScreen} id={"email-body"}>
          <EmailEditor ref={editorHandlerRef} mode="new-email" />
        </Content>
        {editor && <BottomBar mode={"new-email"} editor={editor} />}
        {!fullScreen && (
          <ScheduleBar
            onClose={() => setIsScheduleBarOpen(false)}
            isOpen={isScheduleBarOpen}
            isLoadingRecipient={isLoadingRecipient}
            scheduleEmailHandler={() =>
              modal.pushModal(
                <Warning
                  IsLoadingAtom={IsLoadingSendEmailAtom}
                  icon={"./icons/modal/email-calendar.svg"}
                  title={`This email will be scheduled to send to ${
                    recipient.count
                  } ${
                    recipient.count === 1 ? "contact" : "contacts"
                  } on ${dayjs(scheduleDate).format(
                    "MMM D, YYYY [@] h:mm a (z)"
                  )}`}
                  description="Are you sure you want to schedule it?"
                  buttonsName={["Cancel", "Schedule"]}
                  onOkClicked={() => {
                    handleSchedule();
                  }}
                  onCancelClicked={() => modal.popAll()}
                />
              )
            }
            sendEmailHandler={() =>
              modal.pushModal(
                <Warning
                  IsLoadingAtom={IsLoadingSendEmailAtom}
                  icon={"./send-email-broadcast.svg"}
                  title={`This email will be sent to ${recipient.count} ${
                    recipient.count === 1 ? "contact" : "contacts"
                  }  `}
                  description="Are you sure you want to send this email now?"
                  buttonsName={["Cancel", "Send"]}
                  onOkClicked={() => {
                    handleSend();
                  }}
                  onCancelClicked={() => modal.popAll()}
                />
              )
            }
            saveEmailDraftHandler={handleSaveDraft}
            mode="new-email"
            editor={editor}
          />
        )}
      </Body>
    </>
  );
}
